import axios from "axios";
import { registerChecklistI18n } from "./i18nContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "canales.access_token";
const REFRESH_KEY = "canales.refresh_token";

export const tokenStore = {
  getAccess: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set: (access, refresh) => {
    if (access) localStorage.setItem(TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true, // also send cookies if available (browser permitting)
});

// Attach Authorization header if we have a token (works in iframes / browsers blocking 3rd-party cookies)
api.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers["Authorization"]) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return config;
});

// Try refresh once on 401, then replay the original request
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const orig = error.config || {};
    const status = error.response?.status;
    const isAuthCall = orig.url?.includes("/auth/");
    if (status === 401 && !orig._retry && !isAuthCall) {
      orig._retry = true;
      try {
        if (!refreshing) {
          const rt = tokenStore.getRefresh();
          refreshing = api.post("/auth/refresh", null, {
            headers: rt ? { "X-Refresh-Token": rt } : {},
          });
        }
        const resp = await refreshing;
        refreshing = null;
        if (resp?.data?.access_token) {
          tokenStore.set(resp.data.access_token, resp.data.refresh_token);
        }
        return api.request(orig);
      } catch (e) {
        refreshing = null;
        tokenStore.clear();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);

export const auth = {
  login: async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    if (r.data?.access_token) {
      tokenStore.set(r.data.access_token, r.data.refresh_token);
    }
    return r.data;
  },
  logout: async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    } finally {
      tokenStore.clear();
    }
    return { ok: true };
  },
  me: () => api.get("/auth/me").then((r) => r.data),
  changePassword: (new_password, current_password) =>
    api.post("/auth/change_password", { new_password, current_password }).then((r) => r.data),
};

export const users = {
  list: () => api.get("/users").then((r) => r.data),
  assignable: () => api.get("/users/assignable").then((r) => r.data),
  create: (data) => api.post("/users", data).then((r) => r.data),
  quickCreate: (data) => api.post("/users/quick_create", data).then((r) => r.data),
  patch: (id, data) => api.patch(`/users/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/users/${id}`).then((r) => r.data),
  unlock: (id) => api.post(`/users/${id}/unlock`).then((r) => r.data),
  bulkUnlock: (user_ids) => api.post("/users/bulk_unlock", { user_ids }).then((r) => r.data),
  bulkResetPassword: (user_ids) => api.post("/users/bulk_reset_password", { user_ids }).then((r) => r.data),
};

export const smsOptins = {
  list: () => api.get("/sms/optins").then((r) => r.data),
  link: (id, userId) => api.post(`/sms/optins/${id}/link`, { user_id: userId }).then((r) => r.data),
  dismiss: (id) => api.post(`/sms/optins/${id}/dismiss`).then((r) => r.data),
};

export const stores = {
  list: (viewAll = false) => api.get("/stores", { params: { view_all: viewAll } }).then((r) => r.data),
  create: (data) => api.post("/stores", data).then((r) => r.data),
  bulk: (items, replace = false) =>
    api.post(`/stores/bulk?replace=${replace}`, { items }).then((r) => r.data),
  remove: (id) => api.delete(`/stores/${id}`).then((r) => r.data),
  updateScore: (id, data) => api.patch(`/stores/${id}`, data).then((r) => r.data),
};

export const checklist = {
  list: () =>
    api.get("/checklist").then((r) => {
      // Populate the runtime i18n lookup so user-added categories translate
      // everywhere (findings widgets, PDF, dashboards) without each caller
      // needing to know about the cache.
      try { registerChecklistI18n(r.data); } catch (_) { /* non-fatal */ }
      return r.data;
    }),
  create: (data) => api.post("/checklist", data).then((r) => r.data),
  bulk: (items, replace = false) =>
    api.post(`/checklist/bulk?replace=${replace}`, { items }).then((r) => r.data),
  patch: (id, data) => api.patch(`/checklist/${id}`, data).then((r) => r.data),
  setLogic: (id, logic, flagged_responses = null) =>
    api.patch(`/checklist/${id}/logic`, { logic, flagged_responses }).then((r) => r.data),
  remove: (id) => api.delete(`/checklist/${id}`).then((r) => r.data),
  translate: (target = "en") =>
    api.post(`/checklist/translate?target=${target}`).then((r) => r.data),
};

export const instructionTemplates = {
  list: () => api.get("/instruction_templates").then((r) => r.data),
  create: (title, text) => api.post("/instruction_templates", { title, text }).then((r) => r.data),
  remove: (id) => api.delete(`/instruction_templates/${id}`).then((r) => r.data),
};

export const templates = {
  list: () => api.get("/templates").then((r) => r.data),
  create: (data) => api.post("/templates", data).then((r) => r.data),
  patch: (id, data) => api.patch(`/templates/${id}`, data).then((r) => r.data),
  remove: (id, force = false) => api.delete(`/templates/${id}`, { params: force ? { force: true } : {} }).then((r) => r.data),
  reorder: (ids) => api.post("/templates/reorder", { ids }).then((r) => r.data),
};

export const notifications = {
  // Returns { items, total, unread, offset, limit }
  list: (params = {}) => api.get("/notifications", { params }).then((r) => r.data),
  markRead: (id) => api.patch(`/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () => api.post("/notifications/read_all").then((r) => r.data),
  exportCsv: (params = {}) =>
    api.get("/notifications/export.csv", { params, responseType: "blob" }).then((r) => r.data),
};

export const permissions = {
  list: () => api.get("/permissions").then((r) => r.data),
  me: () => api.get("/permissions/me").then((r) => r.data),
  patch: (role, perms) => api.patch(`/permissions/${role}`, { permissions: perms }).then((r) => r.data),
};

export const audits = {
  create: (data) => api.post("/audits", data).then((r) => r.data),
  list: (params = {}) => api.get("/audits", { params }).then((r) => r.data),
  get: (id) => api.get(`/audits/${id}`).then((r) => r.data),
  patch: (id, data) => api.patch(`/audits/${id}`, data).then((r) => r.data),
  saveResponse: (id, data) => api.post(`/audits/${id}/responses`, data).then((r) => r.data),
  listResponses: (id) => api.get(`/audits/${id}/responses`).then((r) => r.data),
  files: (id) => api.get(`/audits/${id}/files`).then((r) => r.data),
  aiSummary: (id, payload) => api.post(`/audits/${id}/ai_summary`, payload).then((r) => r.data),
  multaAiReason: (id, payload) =>
    api.post(`/audits/${id}/multa_ai_reason`, payload).then((r) => r.data),
  multaNotify: (id, payload) =>
    api.post(`/audits/${id}/multa_notify`, payload).then((r) => r.data),
  share: (id, expiresInDays = null) =>
    api.post(`/audits/${id}/share`, { expires_in_days: expiresInDays }).then((r) => r.data),
  revokeShare: (id) => api.delete(`/audits/${id}/share`).then((r) => r.data),
  publicView: (token) => api.get(`/public/audits/${token}`).then((r) => r.data),
  remove: (id) => api.delete(`/audits/${id}`).then((r) => r.data),
};

export const findings = {
  list: (params = {}) => api.get("/findings", { params }).then((r) => r.data),
  create: (data) => api.post("/findings", data).then((r) => r.data),
  patch: (id, data) => api.patch(`/findings/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/findings/${id}`).then((r) => r.data),
  share: (id) => api.post(`/findings/${id}/share`).then((r) => r.data),
  revokeShare: (id) => api.delete(`/findings/${id}/share`).then((r) => r.data),
  publicView: (token) => api.get(`/public/findings/${token}`).then((r) => r.data),
  publicUpdate: (token, data) => api.patch(`/public/findings/${token}`, data).then((r) => r.data),
  publicUpload: (token, file, author = "") => {
    const fd = new FormData();
    fd.append("file", file);
    if (author) fd.append("author", author);
    return api
      .post(`/public/findings/${token}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  publicTranslateNote: (token, text, target) =>
    api
      .post(`/public/findings/${token}/translate_note`, { text, target })
      .then((r) => r.data),
  sendSms: (id, payload) => api.post(`/findings/${id}/sms`, payload).then((r) => r.data),
  // Grouped checklist: send a single SMS with multiple findings per store
  sendGroupSms: (payload) => api.post(`/findings/sms/group`, payload).then((r) => r.data),
  shareChecklist: (payload) => api.post(`/checklist-groups/share`, payload).then((r) => r.data),
  publicChecklist: (token) => api.get(`/public/checklist-groups/${token}`).then((r) => r.data),
};

export const assignments = {
  list: (params = {}) => api.get("/assignments", { params }).then((r) => r.data),
  get: (id) => api.get(`/assignments/${id}`).then((r) => r.data),
  create: (data) => api.post("/assignments", data).then((r) => r.data),
  patch: (id, data) => api.patch(`/assignments/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/assignments/${id}`).then((r) => r.data),
  reassign: (id, auditor_id, note = "") =>
    api.post(`/assignments/${id}/reassign`, { auditor_id, note }).then((r) => r.data),
  start: (id) => api.post(`/assignments/${id}/start`).then((r) => r.data),
  complete: (id) => api.post(`/assignments/${id}/complete`).then((r) => r.data),
  updateItem: (id, itemId, data) =>
    api.patch(`/assignments/${id}/checklist/${itemId}`, data).then((r) => r.data),
  addComment: (id, data) => api.post(`/assignments/${id}/comments`, data).then((r) => r.data),
  schedule: (params = {}) => api.get("/assignments/schedule", { params }).then((r) => r.data),
  storeHistory: (storeId) => api.get(`/stores/${storeId}/history`).then((r) => r.data),
};

export const sms = {
  status: () => api.get("/sms/status").then((r) => r.data),
  scanNoFollowup: () => api.post("/findings/scan_no_followup").then((r) => r.data),
};

export const ai = {
  help: (message, history = []) =>
    api.post("/ai/help", { message, history }).then((r) => r.data),
};

export const files = {
  upload: (file, audit_id = "", kind = "attachment") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("audit_id", audit_id);
    fd.append("kind", kind);
    return api
      .post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  remove: (id) => api.delete(`/files/${id}`).then((r) => r.data),
  url: (id) => `${API}/files/${id}/download`,
};

export const dashboard = {
  get: (viewAll = false) => api.get("/dashboard", { params: { view_all: viewAll } }).then((r) => r.data),
  activityChart: (params = {}) => api.get("/dashboard/activity_chart", { params }).then((r) => r.data),
};

export const games = {
  list: () => api.get("/games").then((r) => r.data),
  get: (id) => api.get(`/games/${id}`).then((r) => r.data),
  create: (data) => api.post("/games", data).then((r) => r.data),
  update: (id, data) => api.patch(`/games/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/games/${id}`).then((r) => r.data),
  aiGenerate: (id, data) => api.post(`/games/${id}/ai_generate`, data).then((r) => r.data),
  matches: (params = {}) => api.get("/games/matches", { params }).then((r) => r.data),
  recordMatch: (data) => api.post("/games/matches", data).then((r) => r.data),
  deleteMatch: (id) => api.delete(`/games/matches/${id}`).then((r) => r.data),
  publicGame: (token) => api.get(`/public/games/${token}`).then((r) => r.data),
  rooms: {
    create: (data) => api.post("/games/rooms", data).then((r) => r.data),
    get: (code, hostKey) =>
      api.get(`/games/rooms/${code}`, { params: hostKey ? { host_key: hostKey } : {} }).then((r) => r.data),
    join: (code, data) => api.post(`/games/rooms/${code}/join`, data).then((r) => r.data),
    start: (code, hostKey) => api.post(`/games/rooms/${code}/start`, { host_key: hostKey }).then((r) => r.data),
    pick: (code, data) => api.post(`/games/rooms/${code}/pick`, data).then((r) => r.data),
    next: (code, hostKey) => api.post(`/games/rooms/${code}/next`, { host_key: hostKey }).then((r) => r.data),
    resetRound: (code, hostKey) => api.post(`/games/rooms/${code}/reset_round`, { host_key: hostKey }).then((r) => r.data),
    setTurn: (code, hostKey, team) => api.post(`/games/rooms/${code}/set_turn`, { host_key: hostKey, team }).then((r) => r.data),
    end: (code, hostKey) => api.post(`/games/rooms/${code}/end`, { host_key: hostKey }).then((r) => r.data),
  },
};

export function formatApiError(err, fallback = "Error inesperado") {
  if (err?.message === "Network Error") {
    return "No hay conexión con el servidor. Verifica tu red e intenta de nuevo.";
  }
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
